/// <amd-module name="TFS/Admin/Groups/Pivots/Settings/Redux/SettingsPivotReducers" />
import produce from "immer";
import { SettingsPivotActions, SettingsPivotActionTypes } from "TFS/Admin/Groups/Pivots/Settings/Redux/SettingsPivotActions";
import { IGroupsSettingsState, TeamAdmin } from "TFS/Admin/Groups/Common/Contracts";
import { Avatar } from "TFS/Admin/Common/Contracts/VSS.Profile";
import { imageUrlPrefix, SettingsOperation } from "TFS/Admin/Groups/Common/Constants";
import { MessageCardSeverity } from "VSSUI/MessageCard";
import { TeamMember } from "VSS/Platform/Contracts/WebApi";

export function settingsPivotReducer(state: IGroupsSettingsState, action: SettingsPivotActions): IGroupsSettingsState {
    return produce(state || {}, draft => {
        switch (action.type) {
            case SettingsPivotActionTypes.InitView: {
                const { selectedGroup } = action.payload;
                if (selectedGroup) {
                    draft.groupName = selectedGroup.displayName;
                    draft.groupDescription = selectedGroup.description;
                }

                draft.isDirty = false;
                draft.messageCardText = "";
                draft.teamAdmins = [];
                draft.showAddAdminPanel = false;
                break;
            }

            case SettingsPivotActionTypes.InitViewSuccess: {
                const avatar: Avatar = action.payload;
                if (avatar) {
                    draft.currentGroupAvatarUrl = imageUrlPrefix + avatar.value;
                }
                break;
            }

            case SettingsPivotActionTypes.ChangeImageClick: {
                draft.showChangeImageDialog = true;
                break;
            }

            case SettingsPivotActionTypes.CancelImageClick: {
                draft.showChangeImageDialog = false;
                draft.isSaving = false;
                break;
            }

            case SettingsPivotActionTypes.SaveChangesClick: {
                draft.isSaving = true;
                break;
            }

            case SettingsPivotActionTypes.SaveChangesSuccess: {
                draft.isSaving = false;
                draft.isDirty = false;
                break;
            }

            case SettingsPivotActionTypes.SaveChangesFailure: {
                var {resultMessage, severity } = action.payload;
                draft.messageCardText = resultMessage;
                draft.messageCardSeverity = severity;
                draft.isSaving = false;
                break;
            }

            case SettingsPivotActionTypes.GroupNameEdit: {
                draft.groupName = action.payload;
                draft.isDirty = true;
                break;
            }

            case SettingsPivotActionTypes.GroupDescriptionEdit: {
                draft.groupDescription = action.payload;
                draft.isDirty = true;
                break;
            }

            case SettingsPivotActionTypes.SaveImageConfirm: {
                const { resetImage, imageData } = action.payload;
                draft.deleteAvatar = resetImage;
                draft.showChangeImageDialog = false;
                draft.currentGroupAvatarUrl = imageData.url;
                draft.isSaving = true;
                break;
            }

            case SettingsPivotActionTypes.SaveImageFinished: {
                draft.isSaving = false;
                break;
            }

            case SettingsPivotActionTypes.SettingsOperationStatus: {
                var { operation, resultMessage, severity, selectedItem } = action.payload;
                draft.messageCardText = resultMessage;
                draft.messageCardSeverity = severity;

                switch (operation) {
                    case SettingsOperation.DeleteGroup: {
                        if (severity === MessageCardSeverity.Info) {
                            draft.deletedGroup = selectedItem;
                        }
                        break;
                    }
                }
                break;
            }

            case SettingsPivotActionTypes.OnDismissMessageCard: {
                draft.messageCardText = "";
                break;
            }

            case SettingsPivotActionTypes.GetTeamAdminsSuccess: {
                draft.teamAdmins = action.payload;
                break;
            }

            case SettingsPivotActionTypes.RemoveAdminSuccess: {
                const removedAdmin = action.payload;
                draft.teamAdmins = draft.teamAdmins.filter(x => x.identity.descriptor !== removedAdmin.identity.descriptor);
                break;
            }

            case SettingsPivotActionTypes.OpenAddAdminPanel: {
                draft.showAddAdminPanel = true;
                break;
            }

            case SettingsPivotActionTypes.CloseAddAdminPanel: {
                draft.showAddAdminPanel = false;
                break;
            }

            case SettingsPivotActionTypes.SaveAdminsSuccess: {
                const { admins, adminAsMember } = action.payload;
                draft.teamAdmins = draft.teamAdmins.concat(
                    admins.map(
                        x =>
                            ({
                                isMember: adminAsMember,
                                isTeamAdmin: true,
                                identity: {
                                    descriptor: x.subjectDescriptor,
                                    displayName: x.displayName
                                }
                            } as TeamAdmin)
                    )
                );
                draft.showAddAdminPanel = false;
                break;
            }

            default:
                return state;
        }
    });
}
